import { tailwindColors } from '@repo/tailwind-config/tailwind.vars'
import { valueRender } from '@repo/toolkit/conditional-render'
import { DateFormatter } from '@repo/toolkit/date'
import { toFullName } from '@repo/toolkit/to-full-name'
import { useNavigator } from '@repo/toolkit/use-navigator'
import Badge from '@repo/ui/badge'
import DetailedTable, { DetailedTableColumn } from '@repo/ui/detailed-table'
import { Skeleton } from '@repo/ui/skeleton'
import Typography from '@repo/ui/typography'
import { useQuery } from '@tanstack/react-query'

import { getTripFolderAffectedStaffs } from '../../../api/trip-folder/get-trip-folder-affected-staffs'
import { Paths } from '../../../lib/constants/path'
import useTranslate from '../../../lib/hooks/use-translate'
import { staffQualificationEnumMapping } from '../../../lib/types/enums/staff/staff-qualifications.enum'
import { AffectedStaffLight } from '../../../lib/types/models/affectedStaff/affected-staff'
import { TripProperties } from '../../../lib/types/models/trip/properties.trip'
import { LocaleKeys } from '../../../locale/translation-types'
import { formatPhoneNumber } from '../../../utils/functions/format-phone-number'

interface Props {
  folderId: number
}

interface StaffCenterData {
  appointmentTime: string
  centerName: string
  tripLabel: string
  qualification: LocaleKeys
  staff: { id: number; name: string }
  phone: string
  reservationNumber: string
}

const formatStaffData = (data: AffectedStaffLight[]): StaffCenterData[] => {
  return data.flatMap((item) => {
    return item.staffRequest.centers.map((center) => {
      return {
        tripLabel: TripProperties.getTripLabel(item.trip),
        appointmentTime: DateFormatter.getFormattedTime(item.appointmentTime),
        centerName: center.name,
        qualification: staffQualificationEnumMapping[item.staffRequest.qualification].label,
        staff: {
          id: item.staff.id,
          name: toFullName(item.staff.firstName, item.staff.lastName, {
            options: { mode: 'lastname-firstname' }
          })
        },
        phone: valueRender(item.staff.phoneNumber, (phone) => formatPhoneNumber(phone)),
        reservationNumber: item.reservationNumber ?? ''
      }
    })
  })
}

const TripFolderCentersSection = ({ folderId }: Props) => {
  const { t } = useTranslate()
  const { navigate } = useNavigator()

  const { data: staffCenters, isPending } = useQuery({
    queryKey: ['trip-folder-staffs', folderId],
    queryFn: () => getTripFolderAffectedStaffs(folderId),
    select: formatStaffData
  })

  const columns: DetailedTableColumn<StaffCenterData>[] = [
    {
      header: 'Heure de RDV',
      key: 'appointmentTime',
      render: (row) => {
        // appointmentTime may be an empty string but `valueRender` doesn't handle this case, so we need to handle it here
        const label = !!row.appointmentTime ? valueRender(row.appointmentTime) : '-'
        return <Badge text={label} variant={'grey'} />
      }
    },
    {
      header: 'Centre',
      key: 'centerName',
      render: (row) => <Typography variant="secondary-body">{row.centerName}</Typography>
    },
    {
      header: 'Nom et prénom',
      key: 'staff',
      render: (row) => (
        <Typography.Link
          variant="secondary-body"
          onClick={() => navigate(Paths.STAFF_UPDATE_PAGE.replace(':id', row.staff.id.toString()))}
        >
          {row.staff.name}
        </Typography.Link>
      )
    },
    {
      header: 'Téléphone',
      key: 'phone',
      render: (row) => <Typography variant="secondary-body">{row.phone}</Typography>
    },
    {
      header: 'Qualification',
      key: 'qualification',
      borderLeft: true,
      render: (row) => (
        <Badge
          text={t(row.qualification)}
          color={tailwindColors.transparent}
          borderColor={tailwindColors.grey.light.DEFAULT}
          className="first:mt-2 mb-2 h-fit py-1.5 w-max"
        />
      )
    },
    {
      header: 'Voyage',
      key: 'tripLabel',
      render: (row) => <Typography variant="secondary-body">{row.tripLabel}</Typography>
    },
    {
      header: 'N° Résa',
      key: 'reservationNumber',
      render: (row) => <Typography variant="secondary-body">{row.reservationNumber}</Typography>
    }
  ]

  if (isPending) {
    return <Skeleton className="h-[300px] w-full" />
  }

  if (!isPending && (!staffCenters || staffCenters?.length === 0)) {
    return (
      <div className="flex justify-center items-center p-8 bg-gray-50 rounded-3xl border border-primary-light">
        <Typography variant="secondary-body">Aucun encadrants assigné aux centres</Typography>
      </div>
    )
  }

  return (
    <DetailedTable<StaffCenterData>
      groupBy={['appointmentTime', 'centerName']}
      data={staffCenters || []}
      displayGroupByBorder={false}
      headerClassName="px-8"
      columns={columns}
    />
  )
}

export default TripFolderCentersSection
